import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { uploadFile } from 'api'
import {
  fetchPreschoolCentres,
  fetchTeacherTermProgress,
  fetchTeacherWeeklyBundle,
  saveAdminTermNotePhoto,
  saveTeacherTermProgress
} from 'api/preschoolEvaluation'
import { uploadPresets } from 'app/config'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

import { downloadTermReportPdf } from './termReportPdf'

import { MONTHLY_RATING_OPTIONS, PRESCHOOL_LEVEL_OPTIONS } from '../constants'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  spacer: { flexGrow: 1 },
  helpText: { color: theme.palette.text.secondary, fontSize: 12 },
  termTabs: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2)
  },
  card: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  banner: { padding: theme.spacing(1.5, 2), marginBottom: theme.spacing(2) },
  sectionTitle: {
    fontWeight: 600,
    fontSize: 16,
    marginBottom: theme.spacing(1)
  },
  lo: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  loText: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5)
  },
  photoStrip: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  photoThumb: { position: 'relative', width: 64, height: 64 },
  photoImg: {
    width: 64,
    height: 64,
    objectFit: 'cover',
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    display: 'block'
  },
  photoRemove: {
    position: 'absolute',
    top: -8,
    right: -8,
    padding: 2,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': { background: theme.palette.background.paper }
  }
}))

const mondayOf = (dateStr) => {
  const d = dayjs(dateStr)
  if (!d.isValid()) return ''
  const dow = d.day()
  return d.add(dow === 0 ? -6 : 1 - dow, 'day').format('YYYY-MM-DD')
}

// Photos on one skill's term note — thumbnails + add/remove. Persists via the
// enclosing note save (skill_notes image_urls).
const NotePhotos = ({ imageUrls, onAdd, onRemove, disabled }) => {
  const classes = useStyles()
  const inputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const imgs = imageUrls || []

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file) return
    setBusy(true)
    try {
      await onAdd(file)
    } catch (err) {
      showToast('Photo upload failed', 'danger')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box className={classes.photoStrip}>
      {imgs.map((url) => (
        <Box key={url} className={classes.photoThumb}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img src={url} alt="observation" className={classes.photoImg} />
          </a>
          {!disabled && (
            <IconButton
              size="small"
              className={classes.photoRemove}
              onClick={() => onRemove(url)}
              disabled={busy}
            >
              <CloseIcon style={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
      ))}
      {!disabled && (
        <>
          <Button
            size="small"
            variant="outlined"
            onClick={() => inputRef.current && inputRef.current.click()}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : null}
          >
            {busy ? 'Uploading…' : '+ Photo'}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onFile}
          />
        </>
      )}
    </Box>
  )
}

// Terms currently open for filling. Term 2 & 3 are intentionally locked for now
// (greyed out, not clickable) so teachers don't fill them early by mistake;
// re-enable by adding 2 / 3 here when the school year reaches those terms.
const ENABLED_TERMS = [1]

// v2 term report ("term progress") — same data/logic as the teacher app: the
// three terms, each Annual LO marked Beginner/Emerging/Established directly
// (unmarked = NA), plus per-skill notes seeded from that term's weekly
// evaluations. The weekly gate is ADVISORY: any pending weeks are shown as a
// warning (with dates) but don't block editing or publishing. Publish makes the
// term visible to the parent.
// ?centre=<uuid>&level= opens this page on that centre (Quality and Ops
// teacher rows link here, Satya 29 Oct).
const linkParam = (name) => {
  try {
    return new URLSearchParams(window.location.search).get(name) || ''
  } catch (e) {
    return ''
  }
}

const TermReport = () => {
  const classes = useStyles()

  const [centres, setCentres] = useState([])
  const [centre, setCentre] = useState(() => linkParam('centre'))
  const [level, setLevel] = useState(() => linkParam('level') || 'NUR')
  const [dateValue, setDateValue] = useState(dayjs().format('YYYY-MM-DD'))

  const [roster, setRoster] = useState([])
  const [studentUuid, setStudentUuid] = useState('')
  const [data, setData] = useState(null)
  const [selectedTermKey, setSelectedTermKey] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [ratingEdits, setRatingEdits] = useState({}) // {termKey:{loUuid:rating}}
  const [noteEdits, setNoteEdits] = useState({}) // {termKey:{skillId:{text,image_urls}}}
  const [loading, setLoading] = useState(false)

  const weekStart = useMemo(() => mondayOf(dateValue), [dateValue])

  useEffect(() => {
    const run = async () => {
      try {
        const res = await fetchPreschoolCentres()
        setCentres(res?.results || (Array.isArray(res) ? res : []))
      } catch (e) {
        showToast('Failed to load centres', 'danger')
      }
    }
    run()
  }, [])

  const loadRoster = useCallback(async () => {
    if (!centre || !level || !weekStart) {
      setRoster([])
      return
    }
    setStudentUuid('')
    try {
      const bundle = await fetchTeacherWeeklyBundle({
        centre,
        level,
        week_start_date: weekStart
      })
      setRoster(bundle?.roster || [])
    } catch (e) {
      setRoster([])
      if (e?.response?.status === 403) {
        showToast(
          'Teacher login required — live once teacher sign-in is added.',
          'danger'
        )
      }
    }
  }, [centre, level, weekStart])

  useEffect(() => {
    loadRoster()
  }, [loadRoster])

  const loadReport = useCallback(async () => {
    if (!studentUuid) {
      setData(null)
      return
    }
    setLoading(true)
    setRatingEdits({})
    setNoteEdits({})
    try {
      const res = await fetchTeacherTermProgress({
        student: studentUuid,
        week_start_date: weekStart
      })
      setData(res || null)
      const terms = res?.terms || []
      // Only ever auto-select an ENABLED term, so a locked Term 2/3 is never the
      // default selection.
      const enabled = terms.filter((t) => ENABLED_TERMS.includes(t.term))
      const open = enabled.find((t) => t?.gate?.is_open)
      setSelectedTermKey((open || enabled[0] || terms[0])?.term_key || '')
    } catch (e) {
      setData(null)
      showToast(
        e?.response?.status === 403
          ? 'Teacher login required to load.'
          : "Couldn't load this term report",
        'danger'
      )
    } finally {
      setLoading(false)
    }
  }, [studentUuid, weekStart])

  useEffect(() => {
    loadReport()
  }, [loadReport])

  const terms = data?.terms || []
  const skills = data?.skills || []
  const selectedTerm = terms.find((t) => t.term_key === selectedTermKey) || null
  const isPublished = !!selectedTerm?.published
  const editable = !isPublished // the weekly gate no longer blocks editing
  const pendingWeeks = selectedTerm?.gate?.missing_weeks || []

  // ---- ratings ----
  const ratingFor = (lo) => {
    const buffered = ratingEdits[selectedTermKey]?.[lo.uuid]
    if (buffered !== undefined) return buffered
    return (lo.ratings && lo.ratings[selectedTermKey]) ?? ''
  }

  const onSetRating = async (lo, value) => {
    if (!editable) return
    const next = value === '' ? null : value
    setRatingEdits((p) => ({
      ...p,
      [selectedTermKey]: { ...p[selectedTermKey], [lo.uuid]: next }
    }))
    try {
      await saveTeacherTermProgress({
        student: studentUuid,
        term_key: selectedTermKey,
        ratings: [{ annual_lo: lo.uuid, rating: next }]
      })
    } catch (e) {
      showToast(
        e?.response?.status === 403
          ? 'Teacher login required to save.'
          : 'Could not save the rating',
        'danger'
      )
    }
  }

  // ---- per-skill notes (seeded from weekly) ----
  const noteBase = useMemo(() => {
    const m = {}
    ;(selectedTerm?.skill_notes || []).forEach((n) => {
      m[n.skill_id] = n
    })
    return m
  }, [selectedTerm])

  const noteValue = (skillId) => {
    const buffered = noteEdits[selectedTermKey]?.[skillId]
    const base = noteBase[skillId] || {}
    return {
      text: buffered?.text !== undefined ? buffered.text : base.text || '',
      image_urls:
        buffered?.image_urls !== undefined
          ? buffered.image_urls
          : base.image_urls || []
    }
  }

  const bufferNote = (skillId, patch) =>
    setNoteEdits((p) => ({
      ...p,
      [selectedTermKey]: {
        ...p[selectedTermKey],
        [skillId]: { ...noteValue(skillId), ...patch }
      }
    }))

  const persistNote = async (skillId, note) => {
    await saveTeacherTermProgress({
      student: studentUuid,
      term_key: selectedTermKey,
      skill_notes: [
        { skill_id: skillId, text: note.text, image_urls: note.image_urls }
      ]
    })
  }

  const saveNoteText = async (skillId) => {
    if (!editable) return
    try {
      await persistNote(skillId, noteValue(skillId))
    } catch (e) {
      showToast('Could not save the note', 'danger')
    }
  }

  // Photos persist via the admin photos-only endpoint (saveAdminTermNotePhoto),
  // NOT the IsTeacher term-progress PUT — so there is no teacher-login /
  // publish blocker to attaching a photo.
  const addNotePhoto = async (skillId, file) => {
    const uploaded = await uploadFile(file, {
      uploadPreset: uploadPresets[constants.OPENBOOK],
      // Unique per upload — a fixed public_id made every photo overwrite the
      // previous one in Cloudinary (only one photo ever stuck, and the old
      // URL broke). The suffix lets a skill hold multiple distinct photos.
      public_id: `term_${studentUuid}_${skillId}_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}`
    })
    const url = uploaded?.secure_url || uploaded?.url
    if (!url) throw new Error('upload returned no url')
    const current = noteValue(skillId)
    const image_urls = current.image_urls.includes(url)
      ? current.image_urls
      : [...current.image_urls, url]
    bufferNote(skillId, { image_urls })
    const res = await saveAdminTermNotePhoto({
      student: studentUuid,
      term_key: selectedTermKey,
      skill_id: skillId,
      action: 'add',
      url
    })
    // Trust the server's canonical list if it comes back.
    if (res?.image_urls) bufferNote(skillId, { image_urls: res.image_urls })
  }

  const removeNotePhoto = async (skillId, url) => {
    const current = noteValue(skillId)
    const image_urls = current.image_urls.filter((u) => u !== url)
    bufferNote(skillId, { image_urls })
    try {
      const res = await saveAdminTermNotePhoto({
        student: studentUuid,
        term_key: selectedTermKey,
        skill_id: skillId,
        action: 'remove',
        url
      })
      if (res?.image_urls) bufferNote(skillId, { image_urls: res.image_urls })
    } catch (e) {
      showToast('Could not remove the photo', 'danger')
    }
  }

  // ---- publish / unpublish ----
  const setPublish = async (publish) => {
    try {
      await saveTeacherTermProgress({
        student: studentUuid,
        term_key: selectedTermKey,
        [publish ? 'publish' : 'unpublish']: true
      })
      showToast(publish ? 'Published' : 'Unpublished', 'success')
      loadReport()
    } catch (e) {
      showToast(
        e?.response?.status === 403
          ? 'Teacher login required to publish.'
          : e?.message || 'Failed',
        'danger'
      )
    }
  }

  // ---- download the current term report as a PDF ----
  const labelForRating = (val) =>
    MONTHLY_RATING_OPTIONS.find((o) => o.value === val)?.label || val || ''

  const handleDownloadPdf = async () => {
    const s = roster.find((r) => r.student_uuid === studentUuid)
    const studentName = s ? `${s.first_name} ${s.last_name}`.trim() : ''
    const centreName = centres.find((c) => c.uuid === centre)?.name || ''
    const reportSkills = skills.map((group) => ({
      name: group.skill?.name || '',
      los: (group.annual_los || []).map((lo) => ({
        name: lo.name,
        rating: labelForRating(ratingFor(lo))
      })),
      note: noteValue(group.skill.uuid)
    }))
    setDownloading(true)
    try {
      await downloadTermReportPdf({
        studentName,
        centreName,
        // The report's own level: a K2 (UKG) child listed under the LKG roster
        // is reported on K2 LOs, so the picker's level would mislabel the PDF.
        level: data?.level || level,
        termLabel: selectedTerm?.label,
        published: !!selectedTerm?.published,
        skills: reportSkills
      })
    } catch (e) {
      showToast('Could not generate the PDF', 'danger')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.topBar}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel>Centre</InputLabel>
          <Select
            label="Centre"
            value={centre}
            onChange={(e) => setCentre(e.target.value)}
          >
            {centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel>Level</InputLabel>
          <Select
            label="Level"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
          >
            {Object.entries(PRESCHOOL_LEVEL_OPTIONS).map(([code, name]) => (
              <MenuItem key={code} value={code}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Pick any date in the year"
          type="date"
          size="small"
          variant="outlined"
          value={dateValue}
          onChange={(e) => setDateValue(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Box className={classes.spacer} />
        {loading ? <CircularProgress size={22} /> : null}
      </Box>

      {roster.length ? (
        <FormControl
          variant="outlined"
          size="small"
          style={{ minWidth: 260, marginBottom: 16 }}
        >
          <InputLabel>Student ({roster.length})</InputLabel>
          <Select
            label={`Student (${roster.length})`}
            value={studentUuid}
            onChange={(e) => setStudentUuid(e.target.value)}
          >
            {roster.map((s) => (
              <MenuItem key={s.student_uuid} value={s.student_uuid}>
                {s.first_name} {s.last_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Typography className={classes.helpText}>
          Pick a centre, level and a date to load the roster.
        </Typography>
      )}

      {data && studentUuid ? (
        <>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: 8
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={handleDownloadPdf}
              disabled={downloading}
              startIcon={downloading ? <CircularProgress size={14} /> : null}
            >
              {downloading ? 'Preparing…' : 'Download PDF'}
            </Button>
          </Box>
          <Box className={classes.termTabs}>
            {terms.map((t) => {
              const active = t.term_key === selectedTermKey
              const locked = !ENABLED_TERMS.includes(t.term)
              const nPending = (t.gate?.missing_weeks || []).length
              const status = locked
                ? 'Locked'
                : t.published
                ? 'Published'
                : nPending
                ? `${nPending} pending`
                : 'Ready'
              return (
                <Button
                  key={t.term_key}
                  size="small"
                  variant={active ? 'contained' : 'outlined'}
                  color={active ? 'primary' : 'default'}
                  disabled={locked}
                  title={locked ? 'This term will open later' : undefined}
                  onClick={() => !locked && setSelectedTermKey(t.term_key)}
                >
                  {t.label} · {status}
                </Button>
              )
            })}
          </Box>

          {selectedTerm ? (
            <>
              {isPublished ? (
                <Paper
                  variant="outlined"
                  className={classes.banner}
                  style={{ backgroundColor: '#E8F5EC' }}
                >
                  <Typography variant="body2" style={{ color: '#1B7A3D' }}>
                    Published — parents can see this term.
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    style={{ marginTop: 8 }}
                    onClick={() => setPublish(false)}
                  >
                    Unpublish
                  </Button>
                </Paper>
              ) : null}

              {pendingWeeks.length ? (
                <Paper
                  variant="outlined"
                  className={classes.banner}
                  style={{ backgroundColor: '#FFF6E6' }}
                >
                  <Typography variant="body2">
                    ⚠ {pendingWeeks.length} weekly evaluation
                    {pendingWeeks.length > 1 ? 's are' : ' is'} still pending
                    for this term:{' '}
                    {pendingWeeks
                      .map((w) => dayjs(w).format('D MMM'))
                      .join(', ')}
                    . You can still fill and publish — the report will reflect
                    the weeks submitted so far.
                  </Typography>
                </Paper>
              ) : null}

              {skills.map((group) => (
                <Paper
                  key={group.skill.uuid}
                  variant="outlined"
                  className={classes.card}
                >
                  <Typography className={classes.sectionTitle}>
                    {group.skill.name}
                  </Typography>
                  {(group.annual_los || []).map((lo) => (
                    <Box key={lo.uuid} className={classes.lo}>
                      <Typography variant="subtitle2">{lo.name}</Typography>
                      {lo.text ? (
                        <Typography variant="body2" className={classes.loText}>
                          {lo.text}
                        </Typography>
                      ) : null}
                      <FormControl
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 180, marginTop: 4 }}
                        disabled={!editable}
                      >
                        <Select
                          value={ratingFor(lo)}
                          displayEmpty
                          onChange={(e) => onSetRating(lo, e.target.value)}
                        >
                          <MenuItem value="">— NA —</MenuItem>
                          {MONTHLY_RATING_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                </Paper>
              ))}

              {skills.length ? (
                <Paper variant="outlined" className={classes.card}>
                  <Typography className={classes.sectionTitle}>
                    Notes &amp; photos (from this term&apos;s weekly
                    evaluations)
                  </Typography>
                  {/* One row per skill (not only seeded ones) so the +Photo
                      option is always available. Text stays gated by
                      `editable`; photos never are — the admin photo endpoint
                      has no submit/publish blocker. */}
                  {skills.map((group) => {
                    const sid = group.skill.uuid
                    const v = noteValue(sid)
                    return (
                      <Box key={sid} className={classes.lo}>
                        <Typography variant="subtitle2">
                          {group.skill.name}
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          variant="outlined"
                          size="small"
                          margin="dense"
                          disabled={!editable}
                          inputProps={{ style: { position: 'static' } }}
                          value={v.text}
                          onChange={(e) =>
                            bufferNote(sid, { text: e.target.value })
                          }
                          onBlur={() => saveNoteText(sid)}
                        />
                        <NotePhotos
                          imageUrls={v.image_urls}
                          disabled={false}
                          onAdd={(file) => addNotePhoto(sid, file)}
                          onRemove={(url) => removeNotePhoto(sid, url)}
                        />
                      </Box>
                    )
                  })}
                </Paper>
              ) : null}

              {editable ? (
                <Box style={{ marginTop: 8 }}>
                  <Button
                    variant="contained"
                    onClick={() => setPublish(true)}
                    style={{ backgroundColor: '#2d6a4f', color: '#fff' }}
                  >
                    Publish this term
                  </Button>
                  <Typography
                    variant="caption"
                    className={classes.helpText}
                    style={{ display: 'block', marginTop: 6 }}
                  >
                    Ratings and notes save automatically. Publish makes the term
                    visible to the parent.
                  </Typography>
                </Box>
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
    </Box>
  )
}

export default TermReport
